/**
 * Données de l'éditeur avancé d'exercice : chargement de l'exercice et de
 * son contexte (chapitre, leçon, matière), aperçu, import des corrections IA
 * (extrait de ExerciceAdvancedEditor, déplacement pur).
 */
import { useState, useCallback } from 'react';
import ChapitresLeconsApiService from '../../../services/chapitresLeconsApi';
import CurriculumApiService from '../../../services/curriculumApi';
import ExercicesApiService from '../../../services/exercicesApi';
import { Exercice, Chapitre, Lecon } from '../../../types';
import { mergeExerciceData } from '../../../utils/exerciceAIExport';
import { AppEnvironment } from '../../../config/environment';
import { Notifier } from '../../../contexts/NotificationContext';
import { logger } from '../../../lib/logger';

/**
 * Charge l'exercice et son contexte, tient l'aperçu temps réel et fournit
 * l'import des corrections IA (qui met à jour exercice et aperçu).
 * Le chargement n'est pas déclenché ici : l'appelant pose l'effet.
 */
export function useExerciceLoader(
  id: string | undefined,
  environment: AppEnvironment,
  isAdmin: () => boolean,
  notify: Notifier['notify']
) {
  const [exercice, setExercice] = useState<Exercice | null>(null);
  const [chapitreTitre, setChapitreTitre] = useState<string>('');
  const [leconTitre, setLeconTitre] = useState<string>('');
  const [matiereNom, setMatiereNom] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // État pour la prévisualisation en temps réel
  const [previewData, setPreviewData] = useState<Omit<Exercice, 'id'> | null>(null);

  // Fonction de chargement de l'exercice
  const loadExercice = useCallback(async () => {
    if (!id) {
      setError('ID d\'exercice manquant');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [exerciceData, chapitres, lecons] = await Promise.all([
        ExercicesApiService.getExerciceById(environment, id),
        ChapitresLeconsApiService.getChapitres(environment),
        ChapitresLeconsApiService.getLecons(environment),
      ]);

      setExercice(exerciceData);
      setPreviewData(exerciceData);

      // Trouver le titre du chapitre et la matière associée
      const chapitre = chapitres.find((ch: Chapitre) => ch.id === exerciceData.chapitreId);
      if (chapitre) {
        setChapitreTitre(chapitre.titre);

        // Charger la matière du chapitre
        try {
          const matieres = await CurriculumApiService.getMatieres(environment);
          const matiere = matieres.find((m: { id: string; nom: string }) => m.id === chapitre.matiereId);
          if (matiere) {
            setMatiereNom(matiere.nom);
          }
        } catch {
          logger.warn('Impossible de charger la matière');
        }
      }

      // Trouver le titre de la leçon
      const lecon = lecons.find((lec: Lecon) => lec.id === exerciceData.leconId);
      if (lecon) {
        setLeconTitre(lecon.titre);
      }
    } catch (err) {
      logger.error('Erreur lors du chargement de l\'exercice:', err);
      setError('Impossible de charger l\'exercice');
    } finally {
      setLoading(false);
    }
  }, [id, environment]);

  /**
   * Importe les corrections de l'IA
   * Met à jour le formulaire avec les données importées
   * Pour les admins: sauvegarde toujours automatiquement (directement approuvé)
   * Sinon : sauvegarde uniquement si autoSave est true (mode comparaison)
   */
  const handleAIImport = useCallback(async (
    importedData: Partial<Omit<Exercice, 'id'>>,
    options?: { autoSave?: boolean }
  ) => {
    if (!exercice || !id) return;

    // Merge imported data with existing exercise
    const mergedData = mergeExerciceData(exercice, importedData);

    // Update both exercice state and preview
    setExercice(mergedData);
    setPreviewData(mergedData);

    // Auto-save: toujours pour admin, ou si demandé explicitement (mode comparaison)
    const shouldAutoSave = isAdmin() || options?.autoSave;

    if (shouldAutoSave) {
      try {
        // Sauvegarder immédiatement avec les données fusionnées
        await ExercicesApiService.updateExercice(environment, id, mergedData as unknown as Record<string, unknown>);
        notify('Corrections importées et enregistrées', 'success');
      } catch (err) {
        logger.error('[handleAIImport] Erreur sauvegarde:', err);
        notify('Corrections importées mais erreur lors de l\'enregistrement', 'error');
      }
    } else {
      // Sans sauvegarde demandée : juste mettre à jour le formulaire
      notify('Corrections importées avec succès', 'success');
    }
  }, [exercice, id, isAdmin, environment, notify]);

  return {
    exercice, chapitreTitre, leconTitre, matiereNom, loading, error, setError,
    previewData, setPreviewData, loadExercice, handleAIImport,
  };
}
